// Path: src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';

// Core Providers (always needed - available globally)
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoaderProvider } from './context/LoaderContext';
import { ModalProvider } from './context/ModalContext';
import Loader from './components/common/Loader';
import { PendingAccountProvider } from './context/PendingAccountContext';
import { CustomersProvider } from './context/CustomersContext';
import { QueryProvider } from './context/QueryContext';
import { InvoiceProvider } from './context/InvoiceContext';

// Layouts
import AuthLayout from './components/layout/AuthLayout';
import Layout from './components/layout/Layout';

// Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import CreateNewPassword from './pages/auth/CreateNewPassword';
import Dashboard from './pages/dashboard/Dashboard';
import PendingAccounts from './pages/customer_request/PendingAccounts';
import ManageRequest from './pages/customer_request/ManageRequest';
import CustomerList from './pages/customers/CustomerList';
import CustomerDetails from './pages/customers/customer_details/CustomerDetails';
import QueryList from './pages/queries/QueryList';
import CloudTelephony from './pages/cloud_telephony/CloudTelephony'; // Import CloudTelephony component

// Finance Pages
import InvoiceList from './pages/finance/InvoiceList';
import PaymentList from './pages/finance/payments/PaymentList';
import FinanceAnalytics from './pages/finance/Analytics';

// Plans Pages
import PlanList from './pages/plans/PlanList';

// WhatsApp Pages
import MessageTemplates from './pages/whatsapp/MessageTemplates';
import CustomerConfigs from './pages/whatsapp/CustomerConfigs';
import CustomerConfigDetail from './pages/whatsapp/CustomerConfigDetail';

const AuthGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }
  
  return user ? children : <Navigate to="/auth/login" replace />;
};

// Public route guard
const PublicGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }
  
  return !user ? children : <Navigate to="/pending-accounts" replace />;
};

// Wrapper components that provide specific contexts
const DashboardWrapper = ({ children }) => {
  const { DashboardProvider } = require('./context/DashboardContext');
  return <DashboardProvider>{children}</DashboardProvider>;
};

// Main App component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <LoaderProvider>
            <ModalProvider>
              <Suspense fallback={<Loader />}>
                <AppRoutes />
              </Suspense>
            </ModalProvider>
          </LoaderProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

// Routes component
const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }

  return (
    <Routes>
      {/* Authentication Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<PublicGuard><Login /></PublicGuard>} />
        <Route path="forgot-password" element={<PublicGuard><ForgotPassword /></PublicGuard>} />
        <Route path="create-new-password/:uuid64/:token" element={<PublicGuard><CreateNewPassword /></PublicGuard>} />
      </Route>

      {/* Main Application Routes */}
      <Route path="/" element={<AuthGuard><Layout /></AuthGuard>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        {/* Dashboard */}
        <Route path="dashboard" element={
          <DashboardWrapper>
            <Dashboard />
          </DashboardWrapper>
        } />
        
        {/* Customer Management */}
        <Route path="customers" element={<CustomersProvider><CustomerList /></CustomersProvider>} />
        <Route path="customers/:id" element={<InvoiceProvider><CustomerDetails /></InvoiceProvider>} />
        
        {/* Cloud Telephony */}
        <Route path="cloud-telephony" element={<CloudTelephony />} />

        {/* Account Requests */}
        <Route path="pending-accounts" element={<PendingAccountProvider><PendingAccounts /></PendingAccountProvider>} />
        <Route path="manage-requests" element={<ManageRequest />} />
        
        {/* Customer Queries */}
        <Route path="queries" element={<QueryProvider><QueryList /></QueryProvider>} />
        
        {/* Finance Management */}
        <Route path="finance">
          <Route index element={<Navigate to="/finance/invoices" replace />} />
          <Route path="invoices" element={<InvoiceProvider><InvoiceList /></InvoiceProvider>} />
          <Route path="payments" element={<PaymentList />} />
          <Route path="analytics" element={<FinanceAnalytics />} />
        </Route>
        
        {/* Plans Management */}
        <Route path="plans" element={<PlanList />} />

        {/* WhatsApp */}
        <Route path="whatsapp">
          <Route index element={<Navigate to="/whatsapp/masters" replace />} />
          <Route path="masters" element={<MessageTemplates />} />
          <Route path="customer-configs" element={<CustomerConfigs />} />
          <Route path="customer-configs/:id" element={<CustomerConfigDetail />} />
        </Route>
        
        {/* Catch all route */}
        <Route path="*" element={
          user ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth/login" replace />
        } />
      </Route>
    </Routes>
  );
};

export default App;
